import React from "react";
import Image from "next/image";

interface Testimonial {
	quote: string;
	title: string;
	company: string;
}

interface ClientLogo {
	src: string;
	alt: string;
}

interface HomeSocialProofProps {
	sectionTitle?: string;
	testimonials?: Testimonial[];
	clientLogos?: ClientLogo[];
}

const DEFAULT_TESTIMONIALS: Testimonial[] = [
	{
		quote: "Crafted is the only dev partner we trust in front of our clients. They deliver with precision, communicate like pros, and elevate our work every time.",
		title: "Partner",
		company: "Leading Creative Agency",
	},
	{
		quote: "They move faster than any internal team we've ever had — and the product quality is world-class.",
		title: "VP of Product",
		company: "National Brand",
	},
];

const DEFAULT_LOGOS: ClientLogo[] = [
	{ src: "/images/republic.png", alt: "Republic Airways" },
	{ src: "/images/CrewCarwash.png", alt: "Crew Carwash" },
	{ src: "/images/USA.png", alt: "USA Football" },
	{ src: "/images/MISO.png", alt: "MISO Energy" },
];

const HomeSocialProof: React.FC<HomeSocialProofProps> = ({
	sectionTitle = "Proven Across Industries. Chosen by Teams Who Expect More.",
	testimonials = DEFAULT_TESTIMONIALS,
	clientLogos = DEFAULT_LOGOS,
}) => {
	return (
		<div className="bg-black text-white py-20 md:py-28">
			<div className="container mx-auto px-4 max-w-[1280px]">
				{/* Section Title */}
				<h2 className="text-[clamp(24px,2.8vw,40px)] font-bold font-display leading-tight mb-12 md:mb-16">
					{sectionTitle}
				</h2>

				{/* Testimonials Grid */}
				<div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
					{testimonials.map((testimonial, index) => (
						<div
							key={index}
							className="bg-white/5 border border-white/10 rounded-xl p-8"
						>
							<div className="flex items-start mb-6">
								<div className="w-1 bg-blue rounded-full h-full mr-4 self-stretch"></div>
								<p className="text-lg md:text-xl italic text-gray-200 leading-relaxed">
									&ldquo;{testimonial.quote}&rdquo;
								</p>
							</div>
							<div className="pl-5">
								<p className="text-white font-semibold">{testimonial.title}</p>
								<p className="text-gray-400 text-sm">{testimonial.company}</p>
							</div>
						</div>
					))}
				</div>

				{/* Logo Slider */}
				<div>
					<p className="text-lg font-semibold mb-6 text-gray-300">Trusted by:</p>
					<div className="flex flex-wrap justify-start items-center gap-6 md:gap-10 opacity-70">
						{clientLogos.map((logo, index) => (
							<div key={index} className="relative h-10 md:h-14 w-24 md:w-32">
								<Image
									src={logo.src}
									alt={logo.alt}
									layout="fill"
									objectFit="contain"
									className="transition-all duration-300"
								/>
							</div>
						))}
					</div>
				</div>
			</div>
		</div>
	);
};

export default HomeSocialProof;
